// @flow
import React, { type Node, useMemo } from 'react';
import cn from 'classnames';
import { useSelector, shallowEqual } from 'react-redux';
import find from 'ramda/es/find';
import isEmpty from 'ramda/es/isEmpty';
import reduce from 'ramda/es/reduce';
import pipe from 'ramda/es/pipe';
import map from 'ramda/es/map';
import prop from 'ramda/es/prop';
import join from 'ramda/es/join';

import SimpleDate from 'app/dataProviders/SimpleDate';
import Loading from 'app/common/components/Loader';
import { dateFormat } from 'app/pages/routePaths';

import PassagesContainer from '../../containers/Children/PassagesContainer';
import NothingContainer from '../../containers/Children/NothingContainer';
import CardCafeteriaContainer from './CardCafeteria/CardCafeteriaContainer';
import CardMarks from './CardMarks';
import CardTasks from './CardTasks';
import useScheduleDayData from './useScheduleDayData';

import styles from './CardContent.scss';


type Props = {
  klassId: string | null,
  studentId: string,
  date: SimpleDate,
  enablePassages: boolean,
}

const CardsContent = ({
  klassId,
  studentId,
  date,
  enablePassages,
}: Props): Node => {
  const [day, loadingDnevnik] = useScheduleDayData({ klassId, studentId, date });

  const loadingPassages = useSelector(state => state.passages.opts.loading, shallowEqual);
  const loadingCafe = useSelector(state => state.cafeteria.costs.loading, shallowEqual);
  const loading = loadingDnevnik || loadingPassages || loadingCafe;

  const [dayMarks, dayTasks] = useMemo(() => {
    return reduce(([acc1, acc2], { id, title, marks, homeworkTask }) => {
      if (!isEmpty(marks)) {
        const val = pipe(
          map(prop('value')),
          join(', ')
        )(marks);
        acc1.push({
          id,
          text: title,
          val: val.toUpperCase(),
        });
      }

      if (homeworkTask) {
        acc2.push({
          id,
          text: title,
          comment: homeworkTask,
        });
      }
      return [acc1, acc2];
    }, [[], []], day);
  }, [day]);

  const hasMarks = !isEmpty(dayMarks);
  const hasHomework = !!find(({ homeworkTask }) => homeworkTask, day);
  const hasTasks = !isEmpty(dayTasks);

  if (loading) {
    return (
      <Loading className={cn(styles.root, styles.loading)} />
    );
  }

  const dateFormatted = date.getString(dateFormat);

  return (
    <div className={styles.root}>
      {enablePassages
        && <PassagesContainer
          className={styles.card}
          id={studentId}
          date={dateFormatted}
        />}

      <CardCafeteriaContainer
        className={styles.card}
        id={studentId}
        date={date}
      />

      {hasMarks
        && <CardMarks
          className={styles.card}
          studentId={studentId}
          dayMarks={dayMarks}
        />}

      {hasTasks
        && <CardTasks
          className={styles.card}
          studentId={studentId}
          date={date}
          dayTasks={dayTasks}
        />}

      <NothingContainer
        id={studentId}
        date={dateFormatted}
        hasMarks={hasMarks}
        hasHomework={hasHomework}
      />
    </div>
  );
};

export default CardsContent;
